import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { BarChart } from '@mui/x-charts/BarChart'
import { ResponsiveHeatMap } from '@nivo/heatmap'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'

interface TimeInterval {
  interval: number
  timeframe: 'minute' | 'hour'
}

interface TradeTimeAnalysisProps {
  backtestId?: string
  alertId?: string
  accountEquity: number
  selectedGroup: string
  timeIntervals?: TimeInterval[]
}

interface TimeAnalysisData {
  hourlyStats: Array<{
    hour: number
    avgProfit: number
    avgProfitPercentage: number
    totalTrades: number
    winRate: number
    totalProfit: number
  }>
  dailyStats: Array<{
    dayOfWeek: number
    avgProfit: number
    avgProfitPercentage: number
    totalTrades: number
    winRate: number
    totalProfit: number
  }>
  rawTrades: any[]
}

const TradeTimeAnalysis: React.FC<TradeTimeAnalysisProps> = ({
  backtestId,
  alertId,
  selectedGroup,
  accountEquity,
  timeIntervals,
}) => {
  const theme = useTheme()
  const { canViewBacktestInsights } = useApplicationSuiteContext()

  const [timeData, setTimeData] = useState<TimeAnalysisData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [selectedTimeInterval, setSelectedTimeInterval] = useState<TimeInterval>({
    interval: 1,
    timeframe: 'hour',
  })

  const [getBacktestTrades] = useHttpsCallable(firebase_functions, 'getBacktestTrades')

  // Day names for better visualization
  const dayNames = useMemo(
    () => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    []
  )

  // Default time intervals if none provided
  const defaultTimeIntervals = useMemo(
    (): TimeInterval[] => [{ interval: 1, timeframe: 'hour' }],
    []
  )

  // Deduplicate time intervals based on interval and timeframe
  const deduplicatedTimeIntervals = useMemo(() => {
    if (!timeIntervals) return defaultTimeIntervals

    const seen = new Set<string>()
    const unique: TimeInterval[] = []

    for (const timeInterval of timeIntervals) {
      const key = `${timeInterval.interval}-${timeInterval.timeframe}`
      if (!seen.has(key)) {
        seen.add(key)
        unique.push(timeInterval)
      }
    }

    return unique.length > 0 ? unique : defaultTimeIntervals
  }, [timeIntervals, defaultTimeIntervals])

  const availableTimeIntervals = deduplicatedTimeIntervals

  // Update selected time interval when timeIntervals prop changes
  useEffect(() => {
    if (availableTimeIntervals.length > 0) {
      setSelectedTimeInterval(availableTimeIntervals[0])
    }
  }, [availableTimeIntervals])

  // Get the current time interval string for processing
  const getCurrentTimeIntervalString = useCallback(() => {
    const { interval, timeframe } = selectedTimeInterval
    return timeframe === 'minute' ? `${interval}min` : `${interval}hour`
  }, [selectedTimeInterval])

  // Generate title based on time interval
  const getTimeIntervalTitle = useCallback(() => {
    const { interval, timeframe } = selectedTimeInterval
    if (timeframe === 'minute') {
      return interval === 1 ? '1 Minute' : `${interval} Minutes`
    } else {
      return interval === 1 ? '1 Hour' : `${interval} Hours`
    }
  }, [selectedTimeInterval])

  // Handle time interval selection
  const handleTimeIntervalSelection = (timeInterval: TimeInterval) => {
    setSelectedTimeInterval(timeInterval)
  }

  // Analyze trades by time patterns - wrapped in useCallback to prevent unnecessary re-renders
  const analyzeTradesByTime = useCallback(
    (trades: any[]): TimeAnalysisData => {
      // Initialize hourly stats (0-23 hours)
      const hourlyMap = new Map<number, any[]>()
      for (let i = 0; i < 24; i++) {
        hourlyMap.set(i, [])
      }

      // Initialize daily stats (0-6 days, Sunday=0)
      const dailyMap = new Map<number, any[]>()
      for (let i = 0; i < 7; i++) {
        dailyMap.set(i, [])
      }

      // Group trades by hour and day
      trades.forEach((trade) => {
        const entryDate = new Date(trade.entryTime)
        const hour = entryDate.getHours()
        const dayOfWeek = entryDate.getDay()

        hourlyMap.get(hour)?.push(trade)
        dailyMap.get(dayOfWeek)?.push(trade)
      })

      // Calculate hourly statistics
      const hourlyStats = Array.from(hourlyMap.entries()).map(([hour, hourTrades]) => {
        if (hourTrades.length === 0) {
          return {
            hour,
            avgProfit: 0,
            avgProfitPercentage: 0,
            totalTrades: 0,
            winRate: 0,
            totalProfit: 0,
          }
        }

        const totalProfit = hourTrades.reduce((sum, trade) => sum + (trade.profit || 0), 0)
        const profitableTrades = hourTrades.filter((trade) => (trade.profit || 0) > 0)
        const avgProfit = totalProfit / hourTrades.length
        const avgProfitPercentage = accountEquity > 0 ? (avgProfit / accountEquity) * 100 : 0

        return {
          hour,
          avgProfit,
          avgProfitPercentage,
          totalTrades: hourTrades.length,
          winRate: (profitableTrades.length / hourTrades.length) * 100,
          totalProfit,
        }
      })

      // Calculate daily statistics
      const dailyStats = Array.from(dailyMap.entries()).map(([dayOfWeek, dayTrades]) => {
        if (dayTrades.length === 0) {
          return {
            dayOfWeek,
            avgProfit: 0,
            avgProfitPercentage: 0,
            totalTrades: 0,
            winRate: 0,
            totalProfit: 0,
          }
        }

        const totalProfit = dayTrades.reduce((sum, trade) => sum + (trade.profit || 0), 0)
        const profitableTrades = dayTrades.filter((trade) => (trade.profit || 0) > 0)
        const avgProfit = totalProfit / dayTrades.length
        const avgProfitPercentage = accountEquity > 0 ? (avgProfit / accountEquity) * 100 : 0

        return {
          dayOfWeek,
          avgProfit,
          avgProfitPercentage,
          totalTrades: dayTrades.length,
          winRate: (profitableTrades.length / dayTrades.length) * 100,
          totalProfit,
        }
      })

      return { hourlyStats, dailyStats, rawTrades: trades }
    },
    [accountEquity]
  )

  // Fetch and analyze trade data
  useEffect(() => {
    if (!backtestId && !alertId) return
    if ((backtestId && !selectedGroup) || !canViewBacktestInsights) return

    const fetchAndAnalyzeTrades = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const queryParams = backtestId ? { backtestId, tradeGroup: selectedGroup } : { alertId }
        const result: any = await getBacktestTrades(queryParams)

        if (result?.data?.data) {
          const trades = result.data.data.filter(
            (trade: any) =>
              (trade.status === 'CLOSED' || trade.status === 'closed') &&
              trade.exitTime &&
              trade.entryTime
          )

          const analysisData = analyzeTradesByTime(trades)
          setTimeData(analysisData)
        } else {
          setTimeData(null)
        }
      } catch (err) {
        console.error('Error fetching trades for time analysis:', err)
        setError('Failed to load trade time analysis')
        setTimeData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAndAnalyzeTrades()
  }, [
    selectedGroup,
    backtestId,
    alertId,
    getBacktestTrades,
    canViewBacktestInsights,
    analyzeTradesByTime,
  ])

  // Get best and worst performing times
  const timeInsights = useMemo(() => {
    if (!timeData) return null

    const { hourlyStats, dailyStats } = timeData

    // Filter out times with no trades
    const hoursWithTrades = hourlyStats.filter((stat) => stat.totalTrades > 0)
    const daysWithTrades = dailyStats.filter((stat) => stat.totalTrades > 0)

    if (hoursWithTrades.length === 0 || daysWithTrades.length === 0) return null

    // Find best/worst hours by average profit
    const bestHour = hoursWithTrades.reduce((best, current) =>
      current.avgProfit > best.avgProfit ? current : best
    )
    const worstHour = hoursWithTrades.reduce((worst, current) =>
      current.avgProfit < worst.avgProfit ? current : worst
    )

    // Find best/worst days by average profit
    const bestDay = daysWithTrades.reduce((best, current) =>
      current.avgProfit > best.avgProfit ? current : best
    )
    const worstDay = daysWithTrades.reduce((worst, current) =>
      current.avgProfit < worst.avgProfit ? current : worst
    )

    return { bestHour, worstHour, bestDay, worstDay }
  }, [timeData])

  const formatHour = (hour: number): string => {
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}${ampm}`
  }

  const formatPercentage = (percentage: number): string => {
    const num = Math.abs(percentage)
    const numStr = num.toString()

    if (numStr.length >= 3) {
      return `${percentage.toFixed(0)}%`
    } else if (numStr.length === 2) {
      return `${percentage.toFixed(1)}%`
    } else if (numStr.length === 1) {
      return `${percentage.toFixed(2)}%`
    }

    return `${percentage.toFixed(0)}%`
  }

  // Handle day selection from dropdown
  const handleDaySelection = (dayOfWeek: number | null) => {
    setSelectedDay(dayOfWeek)
  }

  // Get time interval in minutes
  const getTimeIntervalMinutes = (interval: string): number => {
    if (interval.endsWith('min')) {
      const minutes = parseInt(interval.replace('min', ''))
      return minutes
    } else if (interval.endsWith('hour')) {
      const hours = parseInt(interval.replace('hour', ''))
      return hours * 60
    }
    return 60 // default to 1 hour
  }

  // Filter data to remove large empty periods while keeping smaller gaps
  const filterDataWithContext = useCallback((allResults: any[], interval: string) => {
    const dataWithTrades = allResults.filter((result) => result.totalTrades > 0)

    if (dataWithTrades.length === 0) {
      return allResults // Return all if no data
    }

    // Find the range of data with trades
    const minIndex = Math.min(...dataWithTrades.map((r) => r.intervalIndex))
    const maxIndex = Math.max(...dataWithTrades.map((r) => r.intervalIndex))

    // Add context bars (2-3 bars on each side)
    const contextBars = 3
    const startIndex = Math.max(0, minIndex - contextBars)
    const endIndex = Math.min(allResults.length - 1, maxIndex + contextBars)

    // Define gap size threshold based on interval type
    const intervalMinutes = getTimeIntervalMinutes(interval)
    const isHourBased = interval.endsWith('hour') && intervalMinutes >= 60

    // For minute-based intervals, only remove gaps larger than 2 hours
    // For hour-based intervals, only remove gaps larger than 6 hours
    const gapThreshold = isHourBased ? 6 : Math.ceil(120 / intervalMinutes) // 120 minutes = 2 hours

    // Create filtered results with context
    const filteredResults = []
    let gapStart = -1
    let gapCount = 0

    for (let i = startIndex; i <= endIndex; i++) {
      const result = allResults[i]

      // Check if this is an empty period within the data range
      if (result.totalTrades === 0 && i > minIndex && i < maxIndex) {
        if (gapStart === -1) {
          gapStart = i
          gapCount = 1
        } else {
          gapCount++
        }
        continue
      }

      // If we were in a gap, decide whether to show it or replace with indicator
      if (gapStart !== -1) {
        if (gapCount >= gapThreshold) {
          // Large gap - replace with gap indicator
          filteredResults.push({
            ...allResults[gapStart],
            isGap: true,
            gapLabel: '...',
          })
        } else {
          // Small gap - keep all the empty bars
          for (let j = gapStart; j < gapStart + gapCount; j++) {
            filteredResults.push(allResults[j])
          }
        }
        gapStart = -1
        gapCount = 0
      }

      filteredResults.push(result)
    }

    // Handle gap at the end
    if (gapStart !== -1) {
      if (gapCount >= gapThreshold) {
        filteredResults.push({
          ...allResults[gapStart],
          isGap: true,
          gapLabel: '...',
        })
      } else {
        for (let j = gapStart; j < gapStart + gapCount; j++) {
          filteredResults.push(allResults[j])
        }
      }
    }

    return filteredResults
  }, [])

  // Group trades by selected time interval
  const getTradesByTimeInterval = useCallback(
    (trades: any[], interval: string) => {
      const intervalMinutes = getTimeIntervalMinutes(interval)
      const intervalMap = new Map<string, any[]>()

      trades.forEach((trade) => {
        const entryDate = new Date(trade.entryTime)
        const minutesFromMidnight = entryDate.getHours() * 60 + entryDate.getMinutes()
        const intervalIndex = Math.floor(minutesFromMidnight / intervalMinutes)
        const intervalKey = `${intervalIndex}`

        if (!intervalMap.has(intervalKey)) {
          intervalMap.set(intervalKey, [])
        }
        intervalMap.get(intervalKey)?.push(trade)
      })

      // For hour-based intervals, calculate the correct number of intervals
      const isHourBased = interval.endsWith('hour') && intervalMinutes >= 60
      const maxIntervals = isHourBased
        ? Math.ceil(1440 / intervalMinutes)
        : Math.ceil(1440 / intervalMinutes) // 1440 minutes in a day

      const allResults = []
      for (let i = 0; i < maxIntervals; i++) {
        const intervalKey = `${i}`
        const intervalTrades = intervalMap.get(intervalKey) || []

        if (intervalTrades.length === 0) {
          allResults.push({
            intervalKey,
            intervalIndex: i,
            avgProfit: 0,
            avgProfitPercentage: 0,
            totalTrades: 0,
            winRate: 0,
            totalProfit: 0,
          })
        } else {
          const totalProfit = intervalTrades.reduce((sum, trade) => sum + (trade.profit || 0), 0)
          const profitableTrades = intervalTrades.filter((trade) => (trade.profit || 0) > 0)
          const avgProfit = totalProfit / intervalTrades.length
          const avgProfitPercentage = accountEquity > 0 ? (avgProfit / accountEquity) * 100 : 0

          allResults.push({
            intervalKey,
            intervalIndex: i,
            avgProfit,
            avgProfitPercentage,
            totalTrades: intervalTrades.length,
            winRate: (profitableTrades.length / intervalTrades.length) * 100,
            totalProfit,
          })
        }
      }

      // For minute-based intervals, filter out large empty periods but keep some context
      if (!isHourBased) {
        return filterDataWithContext(allResults, interval)
      }

      // For hour-based intervals, show all periods throughout the day
      return allResults
    },
    [accountEquity, filterDataWithContext]
  )

  // Format time interval label
  const formatTimeIntervalLabel = (intervalIndex: number, interval: string): string => {
    const intervalMinutes = getTimeIntervalMinutes(interval)
    const isHourBased = interval.endsWith('hour') && intervalMinutes >= 60

    if (isHourBased) {
      // For hour-based intervals, calculate the actual time range
      const startMinutes = intervalIndex * intervalMinutes
      const endMinutes = startMinutes + intervalMinutes

      // Ensure we don't exceed 24 hours (1440 minutes)
      const startHour = Math.floor(startMinutes / 60) % 24
      const endHour = Math.floor(endMinutes / 60) % 24

      const formatHour = (hour: number) => {
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
        return `${displayHour}${ampm}`
      }

      // For single hour intervals, just show the hour
      if (intervalMinutes === 60) {
        return formatHour(startHour)
      }

      // For multi-hour intervals, show the range
      return `${formatHour(startHour)}-${formatHour(endHour)}`
    } else {
      // For minute-based intervals, show the time range
      const startMinutes = intervalIndex * intervalMinutes
      const endMinutes = startMinutes + intervalMinutes

      const startHour = Math.floor(startMinutes / 60)
      const startMin = startMinutes % 60
      const endHour = Math.floor(endMinutes / 60)
      const endMin = endMinutes % 60

      const formatTime = (hour: number, min: number) => {
        const ampm = hour >= 12 ? 'PM' : 'AM'
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
        return `${displayHour}:${min.toString().padStart(2, '0')}${ampm}`
      }

      return `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`
    }
  }

  // Get hourly stats for selected day
  const getHourlyStatsForDay = useCallback(
    (dayOfWeek: number) => {
      if (!timeData?.rawTrades) return []

      // Filter trades for the selected day
      const dayTrades = timeData.rawTrades.filter((trade) => {
        const entryDate = new Date(trade.entryTime)
        return entryDate.getDay() === dayOfWeek
      })

      // Initialize hourly stats for this day
      const hourlyMap = new Map<number, any[]>()
      for (let i = 0; i < 24; i++) {
        hourlyMap.set(i, [])
      }

      // Group trades by hour for this day
      dayTrades.forEach((trade) => {
        const entryDate = new Date(trade.entryTime)
        const hour = entryDate.getHours()
        hourlyMap.get(hour)?.push(trade)
      })

      // Calculate hourly statistics for this day
      return Array.from(hourlyMap.entries()).map(([hour, hourTrades]) => {
        if (hourTrades.length === 0) {
          return {
            hour,
            avgProfit: 0,
            avgProfitPercentage: 0,
            totalTrades: 0,
            winRate: 0,
            totalProfit: 0,
          }
        }

        const totalProfit = hourTrades.reduce((sum, trade) => sum + (trade.profit || 0), 0)
        const profitableTrades = hourTrades.filter((trade) => (trade.profit || 0) > 0)
        const avgProfit = totalProfit / hourTrades.length
        const avgProfitPercentage = accountEquity > 0 ? (avgProfit / accountEquity) * 100 : 0

        return {
          hour,
          avgProfit,
          avgProfitPercentage,
          totalTrades: hourTrades.length,
          winRate: (profitableTrades.length / hourTrades.length) * 100,
          totalProfit,
        }
      })
    },
    [timeData, accountEquity]
  )

  // Calculate actual data range for the heat map
  const getDataRange = useCallback(() => {
    if (!timeData?.rawTrades) return { min: -100, max: 100 }

    const allValues: number[] = []
    dayNames.forEach((_, dayIndex) => {
      const dayStats = getHourlyStatsForDay(dayIndex)
      dayStats.forEach((stat) => {
        if (stat.avgProfit !== 0) {
          allValues.push(stat.avgProfit)
        }
      })
    })

    if (allValues.length === 0) return { min: -100, max: 100 }

    const min = Math.min(...allValues)
    const max = Math.max(...allValues)

    // Add some padding to the range
    const padding = Math.max(Math.abs(min), Math.abs(max)) * 0.1
    return {
      min: Math.floor(min - padding),
      max: Math.ceil(max + padding),
    }
  }, [timeData, getHourlyStatsForDay, dayNames])

  // Single function to get color for any value - used by both heat map and legend
  const getColorForValue = useCallback(
    (value: number) => {
      const dataRange = getDataRange()
      const noDataColor = theme.palette.background.custom3 || '#f5f5f5'

      // Return gray for 0 values (no data)
      if (value === 0) {
        return noDataColor
      }

      // Handle negative values: gradient from gray to red
      if (value < 0) {
        const negativeRange = Math.abs(dataRange.min)
        const intensity = Math.abs(value) / negativeRange // 0 to 1
        const red = Math.round(128 + 127 * intensity) // 128 to 255
        const green = Math.round(128 * (1 - intensity)) // 128 to 0
        const blue = Math.round(128 * (1 - intensity)) // 128 to 0
        return `rgb(${red}, ${green}, ${blue})`
      }

      // Handle positive values: gradient from gray to green
      if (value > 0) {
        const positiveRange = dataRange.max
        const intensity = value / positiveRange // 0 to 1
        // Make green more prominent even for small values
        const red = Math.round(128 * (1 - intensity)) // 128 to 0
        const green = Math.round(128 + 127 * intensity) // 128 to 255
        const blue = Math.round(128 * (1 - intensity)) // 128 to 0

        // Boost green for small positive values to make them more visible
        if (intensity < 0.3) {
          const boost = (0.3 - intensity) * 50 // Extra green boost for small values
          const boostedGreen = Math.min(255, green + boost)
          return `rgb(${red}, ${Math.round(boostedGreen)}, ${blue})`
        }

        return `rgb(${red}, ${green}, ${blue})`
      }

      return noDataColor
    },
    [getDataRange, theme.palette.background.custom3]
  )

  const renderContent = () => (
    <Box>
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Charts and Analysis */}
      {!isLoading && !error && timeData && (
        <Grid container spacing={2}>
          {/* Summary Statistics */}
          <Grid item xs={12}>
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Most Active Hour
                  </Typography>
                  <Typography variant="h6">
                    {timeData.hourlyStats.reduce((max, current) =>
                      current.totalTrades > max.totalTrades ? current : max
                    ).totalTrades > 0 &&
                      formatHour(
                        timeData.hourlyStats.reduce((max, current) =>
                          current.totalTrades > max.totalTrades ? current : max
                        ).hour
                      )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Most Active Day
                  </Typography>
                  <Typography variant="h6">
                    {timeData.dailyStats.reduce((max, current) =>
                      current.totalTrades > max.totalTrades ? current : max
                    ).totalTrades > 0 &&
                      dayNames[
                        timeData.dailyStats.reduce((max, current) =>
                          current.totalTrades > max.totalTrades ? current : max
                        ).dayOfWeek
                      ]}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Hour Win Rate Range
                  </Typography>
                  <Typography variant="h6">
                    {Math.min(
                      ...timeData.hourlyStats.filter((s) => s.totalTrades > 0).map((s) => s.winRate)
                    ).toFixed(1)}
                    % -{' '}
                    {Math.max(
                      ...timeData.hourlyStats.filter((s) => s.totalTrades > 0).map((s) => s.winRate)
                    ).toFixed(1)}
                    %
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Day Win Rate Range
                  </Typography>
                  <Typography variant="h6">
                    {Math.min(
                      ...timeData.dailyStats.filter((s) => s.totalTrades > 0).map((s) => s.winRate)
                    ).toFixed(1)}
                    % -{' '}
                    {Math.max(
                      ...timeData.dailyStats.filter((s) => s.totalTrades > 0).map((s) => s.winRate)
                    ).toFixed(1)}
                    %
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Hourly Analysis */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{ backgroundColor: theme.palette.background.custom3, height: '100%' }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    {selectedTimeInterval.timeframe === 'hour'
                      ? `${selectedTimeInterval.interval} Hour`
                      : `${selectedTimeInterval.interval} Minute`}{' '}
                    Performance
                  </Typography>

                  {availableTimeIntervals.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Interval</InputLabel>
                      <Select
                        value={JSON.stringify(selectedTimeInterval)}
                        label="Interval"
                        onChange={(e) => {
                          const selectedInterval = JSON.parse(e.target.value) as TimeInterval
                          handleTimeIntervalSelection(selectedInterval)
                        }}
                        renderValue={(value) => {
                          const interval = JSON.parse(value as string) as TimeInterval
                          const { interval: intervalValue, timeframe } = interval
                          if (timeframe === 'minute') {
                            return intervalValue === 1 ? '1 Minute' : `${intervalValue} Minutes`
                          } else {
                            return intervalValue === 1 ? '1 Hour' : `${intervalValue} Hours`
                          }
                        }}
                      >
                        {availableTimeIntervals.map((timeInterval, index) => {
                          const { interval: intervalValue, timeframe } = timeInterval
                          const label =
                            timeframe === 'minute'
                              ? intervalValue === 1
                                ? '1 Minute'
                                : `${intervalValue} Minutes`
                              : intervalValue === 1
                                ? '1 Hour'
                                : `${intervalValue} Hours`

                          return (
                            <MenuItem key={index} value={JSON.stringify(timeInterval)}>
                              {label}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                <Box sx={{ height: 300 }}>
                  <BarChart
                    height={300}
                    series={[
                      {
                        data: timeData.rawTrades
                          ? (() => {
                              const intervalStats = getTradesByTimeInterval(
                                timeData.rawTrades,
                                getCurrentTimeIntervalString()
                              )
                              const isHourBased =
                                getCurrentTimeIntervalString().endsWith('hour') &&
                                getTimeIntervalMinutes(getCurrentTimeIntervalString()) >= 60
                              return isHourBased
                                ? intervalStats.map((stat) => stat.avgProfit)
                                : intervalStats
                                    .filter((stat) => !stat.isGap)
                                    .map((stat) => stat.avgProfit)
                            })()
                          : [],
                        label: 'Avg Profit per Trade',
                        color: theme.palette.success.main,
                        valueFormatter: (value, context) => {
                          const intervalStats = timeData.rawTrades
                            ? getTradesByTimeInterval(
                                timeData.rawTrades,
                                getCurrentTimeIntervalString()
                              )
                            : []
                          const isHourBased =
                            getCurrentTimeIntervalString().endsWith('hour') &&
                            getTimeIntervalMinutes(getCurrentTimeIntervalString()) >= 60
                          const filteredStats = isHourBased
                            ? intervalStats
                            : intervalStats.filter((stat) => !stat.isGap)
                          const stat = filteredStats[context.dataIndex || 0]
                          if (stat) {
                            return `$${value?.toFixed(2)} (${formatPercentage(stat.avgProfitPercentage)}) (${stat.totalTrades} trades)`
                          } else {
                            return ''
                          }
                        },
                      },
                    ]}
                    xAxis={[
                      {
                        data: timeData.rawTrades
                          ? (() => {
                              const intervalStats = getTradesByTimeInterval(
                                timeData.rawTrades,
                                getCurrentTimeIntervalString()
                              )
                              const isHourBased =
                                getCurrentTimeIntervalString().endsWith('hour') &&
                                getTimeIntervalMinutes(getCurrentTimeIntervalString()) >= 60
                              return isHourBased
                                ? intervalStats.map((stat) =>
                                    formatTimeIntervalLabel(
                                      stat.intervalIndex,
                                      getCurrentTimeIntervalString()
                                    )
                                  )
                                : intervalStats
                                    .filter((stat) => !stat.isGap)
                                    .map((stat) =>
                                      formatTimeIntervalLabel(
                                        stat.intervalIndex,
                                        getCurrentTimeIntervalString()
                                      )
                                    )
                            })()
                          : [],
                        scaleType: 'band',
                      },
                    ]}
                    yAxis={[
                      {
                        label: 'Average Profit ($)',
                        colorMap: {
                          type: 'piecewise',
                          thresholds: [0],
                          colors: [theme.palette.error.main, theme.palette.success.main],
                        },
                      },
                    ]}
                    grid={{ horizontal: true }}
                    margin={{ left: 70, right: 30, top: 30, bottom: 80 }}
                    slotProps={{
                      bar: {
                        style: {
                          cursor: 'pointer',
                        },
                      },
                    }}
                  />
                </Box>

                {timeData?.rawTrades &&
                  (() => {
                    const currentIntervalString = getCurrentTimeIntervalString()
                    const intervalStats = getTradesByTimeInterval(
                      timeData.rawTrades,
                      currentIntervalString
                    )
                    const intervalsWithTrades = intervalStats.filter(
                      (stat) => stat.totalTrades > 0 && !stat.isGap
                    )

                    if (intervalsWithTrades.length === 0) {
                      return (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center' }}
                        >
                          No trades found for {getTimeIntervalTitle().toLowerCase()} intervals
                        </Typography>
                      )
                    }

                    const bestInterval = intervalsWithTrades.reduce((best, current) =>
                      current.avgProfit > best.avgProfit ? current : best
                    )
                    const worstInterval = intervalsWithTrades.reduce((worst, current) =>
                      current.avgProfit < worst.avgProfit ? current : worst
                    )

                    return (
                      <Box>
                        <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                          Best {getTimeIntervalTitle()}:{' '}
                          {formatTimeIntervalLabel(
                            bestInterval.intervalIndex,
                            currentIntervalString
                          )}{' '}
                          (${bestInterval.avgProfit.toFixed(2)} avg,{' '}
                          {formatPercentage(bestInterval.avgProfitPercentage)},{' '}
                          {bestInterval.totalTrades} trades)
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          Worst {getTimeIntervalTitle()}:{' '}
                          {formatTimeIntervalLabel(
                            worstInterval.intervalIndex,
                            currentIntervalString
                          )}{' '}
                          (${worstInterval.avgProfit.toFixed(2)} avg,{' '}
                          {formatPercentage(worstInterval.avgProfitPercentage)},{' '}
                          {worstInterval.totalTrades} trades)
                        </Typography>
                      </Box>
                    )
                  })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Daily Analysis */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{ backgroundColor: theme.palette.background.custom3, height: '100%' }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {selectedDay !== null && (
                      <IconButton onClick={() => handleDaySelection(null)} sx={{ mr: 1 }}>
                        <ArrowBack />
                      </IconButton>
                    )}
                    <Typography variant="h6">
                      {selectedDay !== null
                        ? `24-Hour Breakdown for ${dayNames[selectedDay]}`
                        : 'Daily Performance'}
                    </Typography>
                  </Box>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>View</InputLabel>
                    <Select
                      value={selectedDay ?? 'daily'}
                      label="View"
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === 'daily') {
                          handleDaySelection(null)
                        } else {
                          handleDaySelection(Number(value))
                        }
                      }}
                      renderValue={(value) => {
                        if (value === 'daily') {
                          return 'Daily'
                        }
                        return dayNames[value as number]
                      }}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      {dayNames.map((dayName, index) => {
                        const dayStats = timeData.dailyStats.find(
                          (stat) => stat.dayOfWeek === index
                        )
                        const tradeCount = dayStats?.totalTrades || 0
                        return (
                          <MenuItem key={index} value={index}>
                            {dayName} {tradeCount > 0 && `(${tradeCount} trades)`}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ height: 300 }}>
                  <BarChart
                    height={300}
                    series={[
                      {
                        data:
                          selectedDay !== null
                            ? getHourlyStatsForDay(selectedDay).map((stat) => stat.avgProfit)
                            : timeData.dailyStats.map((stat) => stat.avgProfit),
                        label: 'Avg Profit per Trade',
                        color: theme.palette.success.main,
                        valueFormatter: (value, context) => {
                          if (selectedDay !== null) {
                            const stats = getHourlyStatsForDay(selectedDay)
                            const stat = stats[context.dataIndex || 0]
                            if (stat) {
                              return `$${value?.toFixed(2)} (${formatPercentage(stat.avgProfitPercentage)}) (${stat.totalTrades} trades)`
                            }
                          } else {
                            const stat = timeData.dailyStats[context.dataIndex || 0]
                            if (stat) {
                              return `$${value?.toFixed(2)} (${formatPercentage(stat.avgProfitPercentage)}) (${stat.totalTrades} trades)`
                            }
                          }
                          return ''
                        },
                      },
                    ]}
                    xAxis={[
                      {
                        data:
                          selectedDay !== null
                            ? getHourlyStatsForDay(selectedDay).map((stat) => formatHour(stat.hour))
                            : timeData.dailyStats.map((stat) =>
                                dayNames[stat.dayOfWeek].slice(0, 3)
                              ),
                        scaleType: 'band',
                      },
                    ]}
                    yAxis={[
                      {
                        label: 'Average Profit ($)',
                        colorMap: {
                          type: 'piecewise',
                          thresholds: [0],
                          colors: [theme.palette.error.main, theme.palette.success.main],
                        },
                      },
                    ]}
                    grid={{ horizontal: true }}
                    margin={{ left: 70, right: 30, top: 30, bottom: 80 }}
                  />
                </Box>

                {timeInsights && (
                  <Box>
                    {selectedDay !== null ? (
                      // Show hourly insights for selected day
                      (() => {
                        const dayStats = getHourlyStatsForDay(selectedDay)
                        const hoursWithTrades = dayStats.filter((stat) => stat.totalTrades > 0)

                        if (hoursWithTrades.length === 0) {
                          return (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textAlign: 'center' }}
                            >
                              No trades found for {dayNames[selectedDay]}
                            </Typography>
                          )
                        }

                        const bestHour = hoursWithTrades.reduce((best, current) =>
                          current.avgProfit > best.avgProfit ? current : best
                        )
                        const worstHour = hoursWithTrades.reduce((worst, current) =>
                          current.avgProfit < worst.avgProfit ? current : worst
                        )

                        return (
                          <>
                            <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                              Best Hour: {formatHour(bestHour.hour)} ($
                              {bestHour.avgProfit.toFixed(2)} avg,{' '}
                              {formatPercentage(bestHour.avgProfitPercentage)},{' '}
                              {bestHour.totalTrades} trades)
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              Worst Hour: {formatHour(worstHour.hour)} ($
                              {worstHour.avgProfit.toFixed(2)} avg,{' '}
                              {formatPercentage(worstHour.avgProfitPercentage)},{' '}
                              {worstHour.totalTrades} trades)
                            </Typography>
                          </>
                        )
                      })()
                    ) : (
                      // Show daily insights
                      <>
                        <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                          Best Day: {dayNames[timeInsights.bestDay.dayOfWeek]} ($
                          {timeInsights.bestDay.avgProfit.toFixed(2)} avg,{' '}
                          {formatPercentage(timeInsights.bestDay.avgProfitPercentage)},{' '}
                          {timeInsights.bestDay.totalTrades} trades)
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          Worst Day: {dayNames[timeInsights.worstDay.dayOfWeek]} ($
                          {timeInsights.worstDay.avgProfit.toFixed(2)} avg,{' '}
                          {formatPercentage(timeInsights.worstDay.avgProfitPercentage)},{' '}
                          {timeInsights.worstDay.totalTrades} trades)
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Hourly Analysis by Day - Stacked Chart */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ backgroundColor: theme.palette.background.custom3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Hourly Performance by Day of Week
                </Typography>

                <Box sx={{ height: 400 }}>
                  <ResponsiveHeatMap
                    data={dayNames.map((dayName, dayIndex) => ({
                      id: dayName.slice(0, 3),
                      data: Array.from({ length: 24 }, (_, hour) => {
                        const dayStats = getHourlyStatsForDay(dayIndex)
                        const hourStat = dayStats.find((stat) => stat.hour === hour)
                        return {
                          x: formatHour(hour),
                          y: hourStat ? hourStat.avgProfit : 0,
                        }
                      }),
                    }))}
                    theme={{
                      axis: {
                        ticks: {
                          text: {
                            fill: 'white',
                          },
                        },
                        legend: {
                          text: {
                            fill: 'white',
                          },
                        },
                      },
                    }}
                    margin={{ top: 60, right: 90, bottom: 60, left: 90 }}
                    valueFormat=">-.2f"
                    axisTop={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -90,
                      legend: '',
                      legendOffset: 46,
                    }}
                    axisRight={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Days',
                      legendPosition: 'middle',
                      legendOffset: 70,
                      style: {
                        legend: {
                          text: {
                            color: 'red',
                          },
                        },
                      },
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Days',
                      legendPosition: 'middle',
                      legendOffset: -72,
                    }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -90,
                      legend: 'Hours',
                      legendPosition: 'middle',
                      legendOffset: 46,
                    }}
                    colors={(cell) => getColorForValue(cell.data.y)}
                    emptyColor={theme.palette.background.custom3}
                    borderColor={{
                      from: 'color',
                      modifiers: [['darker', 0.4]],
                    }}
                    labelTextColor={(cell) =>
                      cell.data.y === 0 ? theme.palette.grey[600] : 'white'
                    }
                    tooltip={({ cell }) => {
                      const dayName = cell.serieId
                      const hour = cell.data.x
                      const profit = cell.data.y
                      const dayIndex = dayNames.findIndex((day) => day.slice(0, 3) === dayName)
                      const dayStats = getHourlyStatsForDay(dayIndex)
                      const hourStat = dayStats.find((stat) => formatHour(stat.hour) === hour)
                      const tradeCount = hourStat?.totalTrades || 0

                      if (profit === 0) {
                        return null
                      }

                      return (
                        <div
                          style={{
                            width: 140,
                            background: theme.palette.background.custom3,
                            padding: '9px 12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          <strong>
                            {dayName} {hour}
                          </strong>
                          <br />
                          Profit: ${profit?.toFixed(2)}
                          <br />
                          Trades: {tradeCount}
                        </div>
                      )
                    }}
                  />
                </Box>

                {/* Heat Map Spectrum Legend */}
                {(() => {
                  const dataRange = getDataRange()

                  // Generate gradient colors using the same function as the heat map
                  const generateGradientColors = () => {
                    const steps = 50 // More steps for smoother gradient
                    const colors: string[] = []

                    for (let i = 0; i <= steps; i++) {
                      const value = dataRange.min + (i / steps) * (dataRange.max - dataRange.min)
                      colors.push(getColorForValue(value))
                    }

                    return colors.join(', ')
                  }

                  return (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Average Profit ($)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ minWidth: 50, textAlign: 'right' }}>
                          ${dataRange.min}
                        </Typography>
                        <Box
                          sx={{
                            width: 200,
                            height: 20,
                            background: `linear-gradient(to right, ${generateGradientColors()})`,
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                          }}
                        ></Box>
                        <Typography variant="caption" sx={{ minWidth: 50, textAlign: 'left' }}>
                          ${dataRange.max}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* No Data State */}
      {!isLoading && !error && !timeData && ((backtestId && selectedGroup) || alertId) && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No trade data available for time analysis.</Typography>
        </Box>
      )}
    </Box>
  )

  if (backtestId && !selectedGroup) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          Select a trade ratio to view time-based analysis.
        </Typography>
      </Box>
    )
  }

  return renderContent()
}

export default TradeTimeAnalysis
